import { type UseSuspenseQueryOptions } from '@tanstack/react-query';
import { QueryKeys } from 'utils/queryKeys';
import { ApiVariablesCustomFieldsRepository } from './ApiVariablesCustomFieldsRepository';
import { ApiDocServiceCustomFieldsRepository } from './ApiDocServiceCustomFieldsRepository';
import { assignedCustomFieldSchema, customFieldSchema, type AssignedCustomField, type CustomField } from './schemas';

const variablesCustomFieldsRepository = new ApiVariablesCustomFieldsRepository();
const docServiceCustomFieldsRepository = new ApiDocServiceCustomFieldsRepository();

type AccountCustomFieldsQueryOptions = UseSuspenseQueryOptions<CustomField[]>;

export type AssignedCustomFieldsQueryOptions = UseSuspenseQueryOptions<AssignedCustomField[]>;

export const customFieldsQueries = {
  listAccountCustomFields: {
    queryKey: QueryKeys.customFields,
    queryFn: async () => {
      const fields = await variablesCustomFieldsRepository.getCustomFields();
      return fields.flatMap((f) => {
        const result = customFieldSchema.safeParse(f);
        return result.success ? [result.data] : [];
      });
    },
    refetchOnWindowFocus: 'always',
  } satisfies AccountCustomFieldsQueryOptions,
  listEditorAssignedCustomFields: (contentId: string, isTemplate = false) => {
    return {
      queryKey: QueryKeys.editorAssignedCustomFields(contentId),
      queryFn: async () => {
        const fields = await docServiceCustomFieldsRepository.getAssignedCustomFields(contentId, isTemplate);
        return fields.flatMap((f) => {
          const result = assignedCustomFieldSchema.safeParse(f);
          return result.success ? [result.data] : [];
        });
      },
    } satisfies AssignedCustomFieldsQueryOptions;
  },
  listUnassignedCustomFields: (contentId: string, isTemplate = false) => ({
    queries: [
      customFieldsQueries.listAccountCustomFields,
      customFieldsQueries.listEditorAssignedCustomFields(contentId, isTemplate),
    ] as const,
    combine: ([allFieldsResult, assignedFieldsResult]: readonly [{ data: CustomField[] }, { data: AssignedCustomField[] }]) => {
      const assignedNames = new Set(assignedFieldsResult.data.map((f) => f.name));
      return allFieldsResult.data.filter((f) => !assignedNames.has(f.name));
    },
  }),
};

export type UnassignedCustomFieldsQueryOptions = ReturnType<typeof customFieldsQueries.listUnassignedCustomFields>;
